import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  CaretRight,
  ChartBar,
  CheckCircle,
  Clock,
  CurrencyDollar,
  GearSix,
  Info,
  Lightning,
  Lock,
  Sparkle,
  Target,
  TrendUp,
  UsersThree
} from '@phosphor-icons/react';

/**
 * Enhanced Roadmap Visual Component
 * 
 * Displays a compelling, tier-aware roadmap that showcases value honestly
 * and encourages upgrades through transparency, not deception.
 * 
 * Features:
 * - Timeline visualization with phase indicators
 * - Top opportunity preview per phase
 * - AI Readiness context
 * - Pro tier teaser with value preview (not hidden)
 * - Context-aware messaging
 */
const RoadmapVisual = ({ 
  opportunities = [],
  aiReadinessScore = 0,
  readinessLabel = 'Developing',
  readinessPillarScores = {},
  isBasicTier = true,
  onUpgrade,
  onViewAll,
  onViewOpportunity
}) => {
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [showPillarBreakdown, setShowPillarBreakdown] = useState(false);
  
  // Pillar definitions - Short names for compact display
  const PILLARS = {
    data: { name: 'Data', fullName: 'Data Readiness', Icon: ChartBar, weight: '30%' },
    technology: { name: 'Tech', fullName: 'Technology', Icon: GearSix, weight: '20%' },
    talent: { name: 'Talent', fullName: 'Talent & Culture', Icon: UsersThree, weight: '25%' },
    strategy: { name: 'Strategy', fullName: 'Strategy', Icon: Target, weight: '15%' },
    resources: { name: 'Budget', fullName: 'Resources', Icon: CurrencyDollar, weight: '10%' }
  };
  
  // Group opportunities by phase
  const quickWins = opportunities.filter(o => o.implementation_phase === 'quick-win');
  const mediumTerm = opportunities.filter(o => o.implementation_phase === 'medium-term');
  const longTerm = opportunities.filter(o => o.implementation_phase === 'long-term');
  
  // Calculate total potential value
  const totalAnnualSavings = opportunities.reduce((sum, opp) => {
    return sum + (opp.roi?.annual_savings || opp.cost_savings_annual || 0);
  }, 0);
  
  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };
  
  // Get readiness modifier for display
  const getReadinessModifier = (score) => {
    if (score >= 80) return { value: 1.0, label: 'Full potential' };
    if (score >= 60) return { value: 0.9, label: '90% potential' };
    if (score >= 45) return { value: 0.8, label: '80% potential' };
    if (score >= 30) return { value: 0.7, label: '70% potential' };
    return { value: 0.6, label: '60% potential' };
  };
  
  // Get color based on score
  const getScoreColor = (score) => {
    if (score >= 75) return '#22C55E'; // green
    if (score >= 50) return '#00E5C0'; // teal
    if (score >= 25) return '#F59E0B'; // amber
    return '#EF4444'; // red
  };
  
  const readinessModifier = getReadinessModifier(aiReadinessScore);
  
  const phases = [
    {
      id: 'quick-win',
      label: 'Quick Wins',
      timeframe: '1-3 months',
      opportunities: quickWins,
      color: '#14F195',
      bgColor: 'from-[#14F195]/20 to-[#14F195]/5',
      borderColor: 'border-[#14F195]/30',
      icon: Lightning,
      description: 'High-impact, low-effort opportunities you can start today'
    },
    {
      id: 'medium-term',
      label: 'Medium-Term',
      timeframe: '3-6 months',
      opportunities: mediumTerm,
      color: '#00E5C0',
      bgColor: 'from-[#00E5C0]/20 to-[#00E5C0]/5',
      borderColor: 'border-[#00E5C0]/30',
      icon: TrendUp,
      description: 'Strategic opportunities requiring moderate effort'
    },
    {
      id: 'long-term',
      label: 'Long-Term',
      timeframe: '6-12 months',
      opportunities: longTerm,
      color: '#F59E0B',
      bgColor: 'from-[#F59E0B]/20 to-[#F59E0B]/5',
      borderColor: 'border-[#F59E0B]/30',
      icon: Target,
      description: 'Transformational projects with significant investment'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Roadmap Header with AI Readiness Context */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Your Implementation Roadmap</h2>
          <p className="text-[#A0AEC0]">
            {opportunities.length} opportunities identified across three phases
          </p>
        </div>
        
        {/* AI Readiness Badge - Redesigned for cleaner layout */}
        <div className="flex items-stretch gap-3">
          <div className="glass rounded-xl p-4 flex items-center gap-3">
            <div className="text-center">
              <div className="text-sm text-[#A0AEC0] mb-1">AI Readiness</div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-white">
                  {aiReadinessScore !== null && aiReadinessScore !== undefined ? aiReadinessScore : '-'}
                </span>
                {aiReadinessScore !== null && aiReadinessScore !== undefined && <span className="text-[#A0AEC0]">/100</span>}
              </div>
              <div className="text-xs text-[#00E5C0] mt-1">
                {readinessLabel || (aiReadinessScore >= 80 ? 'AI Ready' : aiReadinessScore >= 60 ? 'Good Potential' : aiReadinessScore >= 40 ? 'Getting Started' : 'Foundation Building')}
              </div>
            </div>
            
            {/* Pillar Breakdown Toggle */}
            {readinessPillarScores && Object.keys(readinessPillarScores).length > 0 && (
              <button
                onClick={() => setShowPillarBreakdown(!showPillarBreakdown)}
                className="text-xs text-[#00E5C0] hover:text-[#818CF8] transition-colors whitespace-nowrap"
              >
                {showPillarBreakdown ? 'Hide' : 'Show'} breakdown
              </button>
            )}
          </div>
          
          {/* Pillar Breakdown Panel - Only show when expanded, positioned below or inline */}
          <AnimatePresence>
            {showPillarBreakdown && readinessPillarScores && Object.keys(readinessPillarScores).length > 0 && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="glass rounded-xl p-3"
              >
                <div className="flex items-end gap-4">
                  {Object.entries(PILLARS).map(([key, pillar]) => {
                    const score = readinessPillarScores[key] || 0;
                    return (
                      <div key={key} className="text-center min-w-[56px]">
                        <div className="mb-0.5 flex justify-center">
                          <pillar.Icon size={18} className="text-[#A0AEC0]" />
                        </div>
                        <div className="text-[10px] text-[#A0AEC0] mb-1">{pillar.name}</div>
                        <div 
                          className="text-sm font-bold"
                          style={{ color: getScoreColor(score) }}
                        >
                          {score}%
                        </div>
                        <div className="h-1 bg-[rgba(255,255,255,0.1)] rounded-full mt-1">
                          <div 
                            className="h-full rounded-full transition-all"
                            style={{ 
                              width: `${score}%`,
                              backgroundColor: getScoreColor(score)
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          <div className="w-px bg-white/10 self-stretch"></div>
          
          <div className="glass rounded-xl p-4 text-center flex flex-col justify-center">
            <div className="text-sm text-[#A0AEC0] mb-1">Priority Modifier</div>
            <div className="text-xl font-bold text-white">×{readinessModifier.value}</div>
            <div className="text-xs text-[#A0AEC0] mt-1">{readinessModifier.label}</div>
          </div>
          
          {totalAnnualSavings > 0 && (
            <div className="glass rounded-xl p-4 text-center flex flex-col justify-center">
              <div className="text-sm text-[#A0AEC0] mb-1">Total Annual Value</div>
              <div className="text-2xl font-bold bg-gradient-to-r from-[#14F195] to-[#00E5C0] text-transparent bg-clip-text">
                {formatCurrency(totalAnnualSavings)}
              </div>
              <div className="text-xs text-[#A0AEC0] mt-1">potential savings</div>
            </div>
          )}
        </div>
      </div>

      {/* Timeline Visual */}
      <div className="relative">
        <div className="space-y-6">
          {phases.map((phase, phaseIndex) => {
            const PhaseIcon = phase.icon;
            const topOpportunities = phase.opportunities.slice(0, 3);
            const remainingCount = phase.opportunities.length - 3;
            const isExpanded = expandedPhase === phase.id;
            
            return (
              <motion.div
                key={phase.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: phaseIndex * 0.1 }}
                className="relative"
              >
                {/* Phase Card */}
                <div 
                  className={`glass rounded-2xl overflow-hidden border ${phase.borderColor} transition-all duration-300 ${isExpanded ? 'ring-2' : ''}`}
                  style={{ '--tw-ring-color': phase.color }}
                >
                  {/* Phase Header */}
                  <div 
                    className={`p-6 bg-gradient-to-r ${phase.bgColor} cursor-pointer`}
                    onClick={() => setExpandedPhase(isExpanded ? null : phase.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Phase Icon */}
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${phase.color}20` }}
                        >
                          <PhaseIcon size={24} style={{ color: phase.color }} />
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="text-xl font-bold text-white">{phase.label}</h3>
                            <span 
                              className="px-3 py-1 rounded-full text-xs font-medium"
                              style={{ 
                                backgroundColor: `${phase.color}20`,
                                color: phase.color 
                              }}
                            >
                              {phase.timeframe}
                            </span>
                          </div>
                          <p className="text-sm text-[#A0AEC0] mt-1">{phase.description}</p>
                        </div>
                      </div>
                      
                      {/* Count Badge */}
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <div 
                            className="text-3xl font-bold"
                            style={{ color: phase.color }}
                          >
                            {phase.opportunities.length}
                          </div>
                          <div className="text-xs text-[#A0AEC0]">opportunities</div>
                        </div>
                        
                        <CaretRight 
                          size={24} 
                          className={`text-[#A0AEC0] transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  <AnimatePresence>
                    {isExpanded && phase.opportunities.length > 0 && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="p-6 pt-0 space-y-3">
                          {topOpportunities.map((opp, oppIndex) => {
                            const isLocked = isBasicTier && oppIndex > 0;
                            
                            return (
                              <div
                                key={opp.id}
                                className={`p-4 rounded-xl bg-white/5 border border-white/10 transition-all ${
                                  isLocked ? 'opacity-60' : 'hover:bg-white/10 cursor-pointer'
                                }`}
                                onClick={() => !isLocked && onViewOpportunity?.(opp.id)}
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      {isLocked && <Lock size={14} className="text-[#A0AEC0]" />}
                                      <h4 className={`font-medium ${isLocked ? 'text-[#A0AEC0]' : 'text-white'} truncate`}>
                                        {isLocked ? opp.title.slice(0, 25) + '...' : opp.title}
                                      </h4>
                                    </div>
                                    <p className="text-sm text-[#A0AEC0] line-clamp-2">
                                      {isLocked ? 'Upgrade to Builder to view full details' : (opp.one_liner || opp.description?.slice(0, 100))}
                                    </p>
                                  </div>
                                  
                                  {/* Opportunity Potential Badge */}
                                  {opp.opportunity_potential_label && !isLocked && (
                                    <div 
                                      className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium ${
                                        opp.opportunity_potential_label === 'Very High' 
                                          ? 'bg-[#14F195]/20 text-[#14F195]'
                                          : opp.opportunity_potential_label === 'High'
                                          ? 'bg-[#00E5C0]/20 text-[#00E5C0]'
                                          : opp.opportunity_potential_label === 'Medium'
                                          ? 'bg-[#F59E0B]/20 text-[#F59E0B]'
                                          : 'bg-[#718096]/20 text-[#718096]'
                                      }`}
                                    >
                                      {opp.opportunity_potential_label} Potential
                                    </div>
                                  )}
                                  
                                  {/* ROI Preview */}
                                  {opp.roi?.annual_savings && !isLocked && (
                                    <div className="text-right shrink-0">
                                      <div className="text-sm font-semibold text-[#14F195]">
                                        {formatCurrency(opp.roi.annual_savings)}/yr
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          
                          {/* View More / Upgrade CTA */}
                          {(remainingCount > 0 || isBasicTier) && (
                            <div className="pt-2">
                              {isBasicTier && phase.opportunities.length > 1 ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onUpgrade?.();
                                  }}
                                  className="w-full p-4 rounded-xl bg-gradient-to-r from-[#00E5C0]/20 to-[#00E5C0]/20 border border-[#00E5C0]/30 hover:from-[#00E5C0]/30 hover:to-[#00E5C0]/30 transition-all group"
                                >
                                  <div className="flex items-center justify-center gap-3">
                                    <Sparkle size={18} className="text-[#00E5C0]" />
                                    <span className="text-white font-medium">
                                      Unlock {phase.opportunities.length - 1} more opportunities
                                    </span>
                                    <ArrowRight size={18} className="text-[#00E5C0] group-hover:translate-x-1 transition-transform" />
                                  </div>
                                  <p className="text-xs text-[#A0AEC0] mt-2">
                                    Pro includes full opportunity details, ROI calculations, and implementation guides
                                  </p>
                                </button>
                              ) : remainingCount > 0 ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onViewAll?.(phase.id);
                                  }}
                                  className="flex items-center gap-2 text-[#00E5C0] hover:text-[#00D4B0] transition-colors text-sm font-medium"
                                >
                                  View {remainingCount} more
                                  <ArrowRight size={16} />
                                </button>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  {/* Empty State */}
                  {phase.opportunities.length === 0 && (
                    <div className="p-6 pt-0 text-center">
                      <p className="text-[#A0AEC0] text-sm">
                        No opportunities in this phase yet.
                        {phase.id === 'quick-win' && ' This may change as your AI readiness improves.'}
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Context Note */}
      {aiReadinessScore < 60 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-[#00E5C0]/10 border border-[#00E5C0]/30"
        >
          <div className="flex items-start gap-3">
            <Info size={20} className="text-[#00E5C0] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-white font-medium mb-1">
                Your roadmap reflects timing, not importance
              </p>
              <p className="text-sm text-[#A0AEC0]">
                Some high-potential opportunities are in later phases because they need groundwork first. 
                Start with Quick Wins to build momentum — the rest will follow.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Learn More Link */}
      <div className="text-center pt-4">
        <a 
          href="/how-we-score" 
          className="inline-flex items-center gap-2 text-sm text-[#A0AEC0] hover:text-white transition-colors"
        >
          <Info size={16} />
          Understanding your scores
          <ArrowRight size={14} />
        </a>
      </div>
    </div>
  );
};

export default RoadmapVisual;
